import {
    ManagedAgentResponseModel,
    MgmtApi
} from '@/customApiClients/mgmtApiClient/Api';
import { createAutoMemoryStore } from '@/lib/autoMemoryStore';
import { ArrowLeft, Check, Loader2, X } from 'lucide-react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ChainlitContext } from '@chainlit/react-client';

import { MobileNavTrigger } from '@/components/ScheduledAgents/MobileNavTrigger';
import {
    computeNextRuns,
    toQuartzCron,
    WEEKDAYS
} from '@/components/ScheduledAgents/scheduleUtils';
import { StepBasics } from '@/components/ScheduledAgents/StepBasics';
import { StepContext } from '@/components/ScheduledAgents/StepContext';
import { StepInstructions } from '@/components/ScheduledAgents/StepInstructions';
import { StepPublish } from '@/components/ScheduledAgents/StepPublish';
import { StepSchedule } from '@/components/ScheduledAgents/StepSchedule';
import { Button } from '@/components/ui/button';
import {
    Stepper,
    StepperIndicator,
    StepperItem,
    StepperNav,
    StepperSeparator,
    StepperTitle,
    StepperTrigger
} from '@/components/ui/stepper';

import { useAuth } from '@/hooks/useAuth';

const STEPS = [
    { id: 'basics', title: 'Basics' },
    { id: 'context', title: 'Context & tools' },
    { id: 'instructions', title: 'Instructions' },
    { id: 'schedule', title: 'Schedule' },
    { id: 'publish', title: 'Publish' }
];

const FORMAT_INSTRUCTIONS: Record<string, string> = {
    Article:
        'Write the output as a flowing narrative article with headings but do no generate the first primary article header, not a raw data dump. Never generate any ',
    'Short brief':
        'Keep the output short — a few bullet points at most, no more than ~150 words total.',
    'Dashboard digest':
        'Structure the output as a dashboard-style digest, with clearly labeled metric sections rather than prose.'
};

const DEPTH_INSTRUCTIONS: Record<string, string> = {
    'Quick scan':
        'Do a quick scan — check only the most obvious, highest-level metrics, and keep tool calls minimal.',
    Standard:
        'Do a standard-depth analysis using the tools that are directly relevant to the prompt.',
    'Deep dive':
        'Do a deep, thorough analysis — cross-reference multiple data sources and look for secondary patterns, not just the headline numbers.'
};

const LENGTH_INSTRUCTIONS: Record<string, string> = {
    Brief: 'Keep the output brief — aim for roughly 150-250 words.',
    Standard: 'Aim for a moderate output length — roughly 400-600 words.',
    Extensive: 'Write an extensive, detailed output — roughly 800-1200 words.'
};

// The Output format / Analysis depth / Output length pickers have no
// dedicated backend field — the config only stores a single Prompt. So we
// fold the selection into the prompt text itself as extra instructions,
// rather than leaving them cosmetic.
function buildPromptWithInstructions(
    prompt: string,
    format: string,
    depth: string,
    length: string
): string {
    const extra = [
        FORMAT_INSTRUCTIONS[format],
        DEPTH_INSTRUCTIONS[depth],
        LENGTH_INSTRUCTIONS[length]
    ]
        .filter(Boolean)
        .join(' ');
    return extra ? `${prompt}\n\n${extra}` : prompt;
}

export default function ScheduledAgentSetup() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const apiClient = useContext(ChainlitContext);
    const [step, setStep] = useState(0);

    const mgmtApi = useMemo(
        () =>
            new MgmtApi({
                baseUrl: '/mgmt',
                baseApiParams: { credentials: 'include' }
            }),
        []
    );

    const [managedAgents, setManagedAgents] = useState<
        ManagedAgentResponseModel[]
    >([]);
    const [managedAgentIds, setManagedAgentIds] = useState<string[]>([]);
    const [useMemory, setUseMemory] = useState(false);
    const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        mgmtApi.api
            .v1ManagedAgentsAccessibleList()
            .then((res) => setManagedAgents(res?.data?.data || []))
            .catch(() => {});
    }, [mgmtApi]);

    // Form state
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [prompt, setPrompt] = useState('');
    const [format, setFormat] = useState('Article');
    const [depth, setDepth] = useState('Deep dive');
    const [length, setLength] = useState('Standard');
    const [frequency, setFrequency] = useState('Weekly');
    const [weekdays, setWeekdays] = useState<Record<string, boolean>>(
        Object.fromEntries(WEEKDAYS.map((d, i) => [d, i === 0]))
    );
    const [monthday, setMonthday] = useState('1st');
    const [time, setTime] = useState('08:00');
    const [tz, setTz] = useState('Europe/Copenhagen');
    const [visibility, setVisibility] = useState<'private' | 'org'>('private');

    const requiresPrivate = managedAgents.some(
        (a) => managedAgentIds.includes(a.guid) && a.enableRls
    );

    useEffect(() => {
        if (requiresPrivate) setVisibility('private');
    }, [requiresPrivate]);

    const nextRuns = computeNextRuns(frequency, weekdays, monthday, time);

    const toggleWeekday = (day: string) =>
        setWeekdays((prev) => ({ ...prev, [day]: !prev[day] }));

    const isLastStep = step === STEPS.length - 1;

    const handlePublish = async () => {
        if (!name || !prompt) {
            toast.error('Agent name and instructions are required');
            return;
        }
        if (managedAgentIds.length === 0) {
            toast.error('Select an agent');
            return;
        }
        if (frequency === 'Weekly' && !Object.values(weekdays).some(Boolean)) {
            toast.error('Select at least one weekday');
            return;
        }

        setSaving(true);

        try {
            const cronExpression = toQuartzCron(
                frequency,
                weekdays,
                monthday,
                time
            );

            // Persistent memory is a plain yes/no toggle — the store and its
            // one fixed file are created behind the scenes here, named after
            // the job, rather than the user picking a name/path themselves.
            const memory = useMemory
                ? await createAutoMemoryStore(name, mgmtApi)
                : null;

            // Sent once at creation since the mgmt backend has no access to
            // Chainlit's own users table to resolve chatUserId into a name.
            const ownerMeta = (user?.metadata || {}) as Record<string, any>;
            const ownerDisplayName =
                [ownerMeta.given_name, ownerMeta.family_name]
                    .filter(Boolean)
                    .join(' ')
                    .trim() ||
                user?.identifier ||
                undefined;

            const created =
                await mgmtApi.api.v1DeepIntelligenceConfigurationCreate({
                    displayName: name,
                    description: description || null,
                    chatUserId: user?.id ?? '',
                    ownerDisplayName,
                    cronExpression,
                    prompt: buildPromptWithInstructions(
                        prompt,
                        format,
                        depth,
                        length
                    ),
                    // `managedAgentIds` (plural) isn't in the generated
                    // client's request type yet — same reason as `files`
                    // below, cast to bypass the excess-property check until
                    // regenerated.
                    managedAgentIds,
                    isActive: true,
                    isPublic: !requiresPrivate && visibility === 'org',
                    timeZone: tz,
                    memoryStoreId: memory?.memoryStoreId || null,
                    memoryPath: memory?.memoryPath || null,
                    files: selectedFileIds.length
                        ? JSON.stringify(selectedFileIds)
                        : null
                });

            // The creator doesn't automatically get a subscription row — without
            // this, their own freshly-published job wouldn't show up under the
            // Jobs Bank's "Subscribed" filter (subscriptions are a separate
            // concept from ownership, so a job with no subscribers otherwise
            // never appears there even to its own creator).
            const newConfigId = created?.data?.id;
            if (newConfigId) {
                try {
                    await apiClient.post(
                        '/project/deep-intelligence/subscriptions',
                        { configId: newConfigId }
                    );
                } catch {
                    // Non-fatal — the job was created successfully either way.
                }
            }

            toast.success('Job created!');
            navigate('/scheduled-jobs');
        } catch (e: any) {
            toast.error(
                e?.error?.detail ||
                    e?.error?.title ||
                    e?.message ||
                    'Failed to create agent'
            );
        } finally {
            setSaving(false);
        }
    };

    const handleContinue = () => {
        if (isLastStep) {
            void handlePublish();
        } else {
            setStep((s) => Math.min(s + 1, STEPS.length - 1));
        }
    };

    return (
        <div className="flex flex-col bg-background w-full h-full">
            <MobileNavTrigger />
            {/* Back to feed */}
            <div className="top-0 z-10 sticky bg-background px-4 sm:px-6 lg:px-10 shrink-0">
                <div className="mx-auto pt-6 max-w-5xl">
                    <Button
                        onClick={() => navigate('/scheduled-jobs')}
                        variant="ghost"
                        className="group justify-start -ml-3 px-3 w-fit text-foreground hover:text-foreground"
                    >
                        <span className="relative w-4 h-4 overflow-hidden">
                            <ArrowLeft className="absolute inset-0 transition-transform group-hover:-translate-x-full duration-300 ease-out" />

                            <ArrowLeft className="absolute inset-0 transition-transform translate-x-full group-hover:translate-x-0 duration-300 ease-out" />
                        </span>
                        Back to feed
                    </Button>
                </div>
            </div>

            {/* Header */}
            <div className="px-4 sm:px-6 lg:px-10 border-border border-b shrink-0">
                <div className="flex gap-4 mx-auto mb-4 py-4 max-w-5xl">
                    <div>
                        {/* <div
                            className="mb-0.5 font-semibold text-[11px] uppercase tracking-wider"
                            style={{ color: '#2563eb' }}
                        >
                            Scheduled Jobs
                        </div> */}
                        <h1 className="font-medium text-foreground text-2xl lg:text-3xl">
                            Create a new job
                        </h1>
                    </div>
                    {/* <div className="flex items-center gap-3">
                        <Button
                            onClick={() => navigate('/scheduled-jobs')}
                            variant={'outline'}
                            size={'lg'}
                        >
                            <X className="size-4" />
                            Cancel
                        </Button>
                    </div> */}
                </div>
            </div>

            {/* Step rail */}
            <div className="py-5 pt-8 border-border border-b shrink-0">
                <Stepper
                    steps={STEPS}
                    className="flex items-center mx-auto px-4 max-w-agentPageWidth"
                    value={STEPS[step].id}
                    onValueChange={(id) => {
                        const index = STEPS.findIndex((s) => s.id === id);
                        if (index !== -1) setStep(index);
                    }}
                >
                    <StepperNav>
                        {STEPS.map((s, i) => (
                            <StepperItem
                                key={s.id}
                                stepId={s.id}
                                className="relative flex-1 min-w-0"
                            >
                                <StepperTrigger className="flex-col gap-2.5">
                                    <StepperIndicator>{i + 1}</StepperIndicator>
                                    <StepperTitle>{s.title}</StepperTitle>
                                </StepperTrigger>
                                {i < STEPS.length - 1 && (
                                    <StepperSeparator className="top-2 right-[calc(-50%+1rem)] left-[calc(50%+1rem)] absolute" />
                                )}
                            </StepperItem>
                        ))}
                    </StepperNav>
                </Stepper>
            </div>

            {/* Scrollable step content */}
            <div className="flex-1 px-4 sm:px-6 lg:px-10 overflow-y-auto">
                <div className="mx-auto py-10 max-w-5xl">
                    {step === 0 && (
                        <StepBasics
                            name={name}
                            setName={setName}
                            description={description}
                            setDescription={setDescription}
                        />
                    )}
                    {step === 1 && (
                        <StepContext
                            managedAgents={managedAgents}
                            managedAgentIds={managedAgentIds}
                            setManagedAgentIds={setManagedAgentIds}
                            useMemory={useMemory}
                            setUseMemory={setUseMemory}
                            selectedFileIds={selectedFileIds}
                            setSelectedFileIds={setSelectedFileIds}
                        />
                    )}
                    {step === 2 && (
                        <StepInstructions
                            prompt={prompt}
                            setPrompt={setPrompt}
                            format={format}
                            setFormat={setFormat}
                            depth={depth}
                            setDepth={setDepth}
                            length={length}
                            setLength={setLength}
                        />
                    )}
                    {step === 3 && (
                        <StepSchedule
                            frequency={frequency}
                            setFrequency={setFrequency}
                            weekdays={weekdays}
                            toggleWeekday={toggleWeekday}
                            monthday={monthday}
                            setMonthday={setMonthday}
                            time={time}
                            setTime={setTime}
                            tz={tz}
                            setTz={setTz}
                            nextRuns={nextRuns}
                        />
                    )}
                    {step === 4 && (
                        <StepPublish
                            visibility={visibility}
                            setVisibility={setVisibility}
                            agentName={name || 'Franchise Sales Digest'}
                            selectedAgentName={
                                managedAgents
                                    .filter((a) =>
                                        managedAgentIds.includes(a.guid)
                                    )
                                    .map((a) => a.displayName)
                                    .join(', ') || undefined
                            }
                            userLabel={user?.identifier ?? user?.id}
                            rlsEnabled={requiresPrivate}
                        />
                    )}

                    {/* Step navigation — sits right under the step's own
                        content instead of a page-wide docked footer, so it
                        stays part of the same centered form group. */}
                    <div className="mt-8 pt-6 border-border border-t">
                        <div className="flex justify-between items-center gap-2">
                            <Button
                                variant="tertiary"
                                onClick={() =>
                                    setStep((s) => Math.max(s - 1, 0))
                                }
                                disabled={step === 0}
                                size={'default'}
                                className="px-4"
                            >
                                Back
                            </Button>
                            <Button
                                onClick={handleContinue}
                                disabled={saving}
                                size={'default'}
                            >
                                {isLastStep ? (
                                    saving ? (
                                        <>
                                            <Loader2 className="animate-spin" />
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <Check
                                               
                                                strokeWidth={2.5}
                                            />
                                            Create &amp; publish
                                        </>
                                    )
                                ) : (
                                    'Continue'
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
